//@ts-check
import React from 'react';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon } from '@heroicons/react/24/outline';

import { calculateDeltaPercentage } from '@datahooks/metadata/DataUtils';
import StyledTooltip from '@components/general/StyledTooltip';

import PropTypes from 'prop-types';

/**
 * DeltaChip component displays the percentage change between a current value and a previous value, along with an arrow indicating the direction of the change. It is styled with different colors for positive and negative changes.
 *
 * @param {Object} props
 * @param {number} props.currentValue - The current value to compare.
 * @param {number} props.previousValue - The previous value to compare against.
 * @param {string} [props.dataTestId] - Optional data-testid for testing purposes.
 * @param {'up'|'down'} [props.goodDirection='up'] - Indicates whether an increase ('up') or decrease ('down') is considered a positive change for styling purposes.
 * @param {'percentage'|'absolute'} [props.labelType='percentage'] - Determines whether the delta should be calculated and displayed as a percentage or an absolute value.
 *
 * @returns {JSX.Element} The rendered DeltaChip component.
 */

export default function DeltaChip({ currentValue, previousValue, dataTestId = 'delta-chip', goodDirection = 'up', labelType = 'percentage' }) {
  // Don't render the chip if either value is missing
  if (currentValue === undefined || previousValue === undefined) {
    return null;
  }

  let delta = currentValue - previousValue;

  if (labelType === 'percentage') {
    delta = calculateDeltaPercentage(currentValue, previousValue);

    if (delta === null) {
      // Cannot calculate percentage delta (e.g., previousValue is zero or invalid)
      return null;
    }
  }

  let tooltipContent = 'No change';
  const isGoodDelta = goodDirection === 'up' ? delta >= 0 : delta <= 0;

  if (delta != 0) {
    const changeDirection = delta > 0 ? 'Increase' : 'Decrease';
    tooltipContent = `${changeDirection} of ${Math.abs(delta)}${labelType === 'percentage' ? '%' : ''}`;
    tooltipContent += ` compared to previous value of ${previousValue}`;
  }

  return (
    <StyledTooltip content={tooltipContent}>
      <span
        data-testid={dataTestId}
        className={`inline-flex items-center gap-1 text-sm px-1.5 py-0.5 rounded-md border ${
          isGoodDelta
            ? 'border-green-200 text-black bg-green-50 dark:border-green-700 dark:text-green-100 dark:bg-green-900'
            : 'border-red-200 text-black bg-red-50 dark:border-red-700 dark:text-red-100 dark:bg-red-900'
        }`}
      >
        {delta > 0 && <ArrowTrendingUpIcon className="h-3 w-3" />}
        {delta < 0 && <ArrowTrendingDownIcon className="h-3 w-3" />}
        {delta}
        {labelType === 'percentage' && '%'}
      </span>
    </StyledTooltip>
  );
}

DeltaChip.propTypes = {
  currentValue: PropTypes.number.isRequired,
  previousValue: PropTypes.number.isRequired,
  dataTestId: PropTypes.string,
  goodDirection: PropTypes.oneOf(['up', 'down']),
  labelType: PropTypes.oneOf(['percentage', 'absolute']),
};
